<template>
  <section
    class="cppc"
    :class="{ 'cppc--loading': loading }"
    data-testid="current-pay-plan-card"
  >
    <header class="cppc__title-row">
      <h2 class="cppc__title">Current Pay Plan ({{ payPlanCode }})</h2>
      <button
        v-if="showClose"
        type="button"
        class="cppc__close-btn"
        aria-label="Close current pay plan"
        @click="$emit('close')"
      >
        <img :src="xCloseIcon" alt="" />
      </button>
    </header>

    <div class="cppc__summary">
      <div class="cppc__summary-header">
        <p class="cppc__meta-line">
          Effective from: {{ formattedEffectiveDate
          }}<template v-if="version">, {{ version }}</template>
        </p>

        <div class="cppc__header-actions">
          <button
            v-if="canAssessIncrease"
            type="button"
            class="cppc__btn cppc__btn--tertiary"
            :disabled="loading"
            @click="$emit('assess-increase')"
          >
            <span class="cppc__btn-icon" aria-hidden="true">
              <img :src="searchIcon" alt="" />
            </span>
            <span class="cppc__btn-label">Assess Increase</span>
          </button>

          <button
            v-if="canProposeIncrease"
            type="button"
            class="cppc__btn cppc__btn--secondary"
            :disabled="loading"
            @click="$emit('propose-increase')"
          >
            <span class="cppc__btn-icon" aria-hidden="true">
              <img :src="plusSecondaryIcon" alt="" />
            </span>
            <span class="cppc__btn-label">Propose Increase</span>
          </button>

          <button
            v-if="canIncrease"
            type="button"
            class="cppc__btn cppc__btn--primary"
            :disabled="loading"
            @click="$emit('increase')"
          >
            <span class="cppc__btn-icon" aria-hidden="true">
              <img :src="plusPrimaryIcon" alt="" />
            </span>
            <span class="cppc__btn-label">Increase</span>
          </button>

          <div class="cppc__icon-actions">
            <button
              type="button"
              class="cppc__icon-btn"
              aria-label="Open chatter"
              :disabled="loading"
              @click="$emit('open-chatter')"
            >
              <ChatterIcon :unread="chatterUnread" />
            </button>

            <MenuIcon
              :dropdownItems="menuItems"
              :showTooltip="false"
              vertical
              class="cppc__menu-icon"
              @onClick="onMenuAction"
            />
          </div>
        </div>
      </div>

      <div class="cppc__metric-row">
        <article class="cppc__metric-tile cppc__metric-tile--salary">
          <p class="cppc__metric-label">Base Salary</p>
          <p v-if="!loading" class="cppc__metric-value">{{ baseSalaryFormatted }}</p>
          <div v-else class="cppc__skeleton cppc__skeleton--value" aria-hidden="true" />
          <button
            v-if="canEditBaseSalary"
            type="button"
            class="cppc__edit-btn"
            aria-label="Edit base salary"
            :disabled="loading"
            @click="$emit('edit-base-salary')"
          >
            <img :src="editIcon" alt="" />
          </button>
        </article>
      </div>

      <div
        v-if="deltaBannerText"
        class="cppc__delta-banner"
        :class="`cppc__delta-banner--${deltaBannerVariant}`"
      >
        {{ deltaBannerText }}
      </div>

      <div class="cppc__lower-block">
        <div class="cppc__metric-row">
          <article class="cppc__metric-tile cppc__metric-tile--fair-pay">
            <p class="cppc__metric-label">Fair Pay today</p>
            <p v-if="!loading" class="cppc__metric-value">{{ fairPayTodayFormatted }}</p>
            <div v-else class="cppc__skeleton cppc__skeleton--value" aria-hidden="true" />
          </article>

          <article class="cppc__metric-tile cppc__metric-tile--readiness">
            <div class="cppc__readiness">
              <div class="cppc__readiness-row">
                <span class="cppc__readiness-pct">{{ readinessPctDisplay }}%</span>
                <span class="cppc__readiness-label">Readiness</span>
                <span class="cppc__readiness-as-of">{{ readinessAsOf }}</span>
              </div>
              <div class="cppc__readiness-track" aria-hidden="true">
                <div
                  class="cppc__readiness-fill"
                  :style="{ width: readinessBarWidth }"
                />
              </div>
            </div>
          </article>
        </div>

        <div class="cppc__position-strip">
          <p class="cppc__position-caption">
            Employee primary position and its pay range today
          </p>

          <button
            type="button"
            class="cppc__position-link"
            @click="$emit('open-position', primaryPosition.code)"
          >
            <PositionIcon size="small" class="cppc__position-icon" />
            <span>{{ primaryPositionLabel }}</span>
          </button>

          <p class="cppc__readiness-ladder">
            <span>@ 0%, </span>
            <span class="cppc__tone--danger">50%</span>
            <span>, </span>
            <span class="cppc__tone--success">100%</span>
            <span>, </span>
            <span class="cppc__tone--info">150%</span>
            <span> readiness</span>
          </p>

          <div class="cppc__pay-range-block">
            <button
              type="button"
              class="cppc__pay-range-link"
              @click="$emit('open-pay-range', payRange.code)"
            >
              <img :src="payRangeIcon" alt="" class="cppc__pay-range-icon" />
              <span class="cppc__pay-range-code">{{ payRange.code }}</span>
              <span class="cppc__pay-range-name">{{ payRange.name }}</span>
            </button>

            <div class="cppc__pay-range-meta">
              <div class="cppc__pay-range-meta-left">
                <img
                  v-if="flagSrc"
                  :src="flagSrc"
                  alt=""
                  class="cppc__flag"
                />
                <span>{{ payRange.country }}</span>
                <img :src="metaSepIcon" alt="" class="cppc__meta-sep" aria-hidden="true" />
                <span>{{ payRange.workMode }}</span>
                <img :src="metaSepIcon" alt="" class="cppc__meta-sep" aria-hidden="true" />
                <span>{{ payRange.currencyUnit }}</span>
                <img :src="metaSepIcon" alt="" class="cppc__meta-sep" aria-hidden="true" />
              </div>

              <div class="cppc__ladder-values">
                <span
                  v-for="(value, index) in payRangeValues"
                  :key="`${index}-${value}`"
                  class="cppc__ladder-value"
                  :class="ladderToneClass(index)"
                >
                  <span v-if="index > 0" class="cppc__ladder-dash">-</span>
                  {{ value }}
                </span>
              </div>

              <img :src="metaSepIcon" alt="" class="cppc__meta-sep" aria-hidden="true" />

              <div class="cppc__version-wrap">
                <span v-if="payRange.version" class="cppc__version-badge">{{
                  payRange.version
                }}</span>
                <span v-if="payRange.asOf" class="cppc__version-date"
                  >as of {{ formattedPayRangeAsOf }}</span
                >
              </div>
            </div>
          </div>
        </div>

        <div v-if="fromProposal && proposal" class="cppc__reconciliation-footer">
          <p class="cppc__reconciliation-text">
            Reconciled from proposal #{{ proposal.code }} on
            {{ formattedReconciledOn }} by {{ proposal.reconciledBy }}
          </p>
          <button
            type="button"
            class="cppc__expand-btn"
            aria-label="Open originating proposal"
            :disabled="loading"
            @click="$emit('open-proposal', proposal.code)"
          >
            <img :src="expandIcon" alt="" />
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";

import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";
import PositionIcon from "@components/svgs/PositionIcon.vue";
import editIcon from "@components/assets/edit-03.svg";
import xCloseIcon from "@components/assets/x-close.svg";
import metaSepIcon from "@components/PayRangeCard/meta-sep.svg";
import { formatFairPayDate } from "@components/utils/fairPayFormat";
import searchIcon from "./icons/search-sm.svg";
import plusSecondaryIcon from "./icons/plus-secondary.svg";
import plusPrimaryIcon from "./icons/plus-primary.svg";
import payRangeIcon from "./icons/pay-range.svg";
import expandIcon from "./icons/expand-05.svg";
import {
  buildDeltaBannerText,
  formatMoneyAbbreviated,
  formatReadinessLadderValue,
  getDeltaBannerVariant,
  resolveFlagSrc,
} from "./currentPayPlanCardUtils";

const props = defineProps({
  payPlanCode: {
    type: String,
    required: true,
  },
  effectiveDate: {
    type: [String, Date],
    required: true,
  },
  version: {
    type: String,
    default: "",
  },
  baseSalary: {
    type: Number,
    required: true,
  },
  currency: {
    type: String,
    default: "USD",
  },
  fairPayToday: {
    type: Number,
    required: true,
  },
  fairPayDelta: {
    type: Object,
    required: true,
    validator(value) {
      return (
        value &&
        typeof value.amount === "number" &&
        typeof value.pct === "number" &&
        ["below", "above", "at"].includes(value.direction) &&
        typeof value.withinTolerance === "boolean"
      );
    },
  },
  readinessPct: {
    type: Number,
    required: true,
    validator: (value) => value >= 0 && value <= 150,
  },
  readinessAsOf: {
    type: String,
    default: "today",
  },
  primaryPosition: {
    type: Object,
    required: true,
    validator(value) {
      return value && typeof value.code === "string" && typeof value.name === "string";
    },
  },
  payRange: {
    type: Object,
    required: true,
    validator(value) {
      return (
        value &&
        typeof value.code === "string" &&
        typeof value.name === "string" &&
        Array.isArray(value.values) &&
        value.values.length === 4
      );
    },
  },
  fromProposal: {
    type: Boolean,
    default: false,
  },
  proposal: {
    type: Object,
    default: null,
  },
  canAssessIncrease: {
    type: Boolean,
    default: true,
  },
  canProposeIncrease: {
    type: Boolean,
    default: true,
  },
  canIncrease: {
    type: Boolean,
    default: true,
  },
  canEditBaseSalary: {
    type: Boolean,
    default: false,
  },
  chatterUnread: {
    type: Number,
    default: 0,
  },
  menuItems: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  showClose: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "assess-increase",
  "propose-increase",
  "increase",
  "edit-base-salary",
  "open-chatter",
  "open-position",
  "open-pay-range",
  "open-proposal",
  "menu-action",
  "close",
]);

const formattedEffectiveDate = computed(() => formatFairPayDate(props.effectiveDate));

const baseSalaryFormatted = computed(() =>
  formatMoneyAbbreviated(props.baseSalary, props.currency)
);

const fairPayTodayFormatted = computed(() =>
  formatMoneyAbbreviated(props.fairPayToday, props.currency)
);

const deltaBannerText = computed(() =>
  buildDeltaBannerText(
    props.baseSalary,
    props.fairPayToday,
    props.fairPayDelta,
    props.readinessPct
  )
);

const deltaBannerVariant = computed(() =>
  getDeltaBannerVariant(props.baseSalary, props.fairPayToday, props.fairPayDelta)
);

const readinessPctDisplay = computed(() => Math.round(Number(props.readinessPct) || 0));

const readinessBarWidth = computed(() => {
  const pct = Math.max(0, Math.min(150, Number(props.readinessPct) || 0));
  return `${(pct / 150) * 100}%`;
});

const primaryPositionLabel = computed(() => {
  const { code, name } = props.primaryPosition;
  return name ? `${code}-${name}` : code;
});

const flagSrc = computed(() => resolveFlagSrc(props.payRange.countryCode));

const payRangeValues = computed(() =>
  (props.payRange.values || []).map((value) => formatReadinessLadderValue(value))
);

const formattedPayRangeAsOf = computed(() => formatFairPayDate(props.payRange.asOf));

const formattedReconciledOn = computed(() =>
  props.proposal?.reconciledOn ? formatFairPayDate(props.proposal.reconciledOn) : ""
);

const LADDER_TONE_CLASSES = ["cppc__tone--neutral", "cppc__tone--danger", "cppc__tone--success", "cppc__tone--info"];

function ladderToneClass(index) {
  return LADDER_TONE_CLASSES[index] || "cppc__tone--neutral";
}

function onMenuAction(item) {
  emit("menu-action", item?.value || item?.name || "more");
}
</script>

<style scoped>
.cppc {
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
  padding: 24px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #ffffff;
  box-sizing: border-box;
}

.cppc__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.cppc__close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.cppc__close-btn img {
  display: block;
  width: 16px;
  height: 16px;
}

.cppc__title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
  color: #101828;
}

.cppc__summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 16px;
  border-radius: 10px;
  background: #f9fafb;
  box-sizing: border-box;
}

.cppc__summary-header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 0;
}

.cppc__meta-line {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
  white-space: nowrap;
}

.cppc__header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.cppc__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid transparent;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  box-sizing: border-box;
}

.cppc__btn--tertiary {
  padding: 0;
  border: none;
  box-shadow: none;
  background: transparent;
  color: #6941c6;
}

.cppc__btn--secondary {
  background: #ffffff;
  border-color: #d6bbfb;
  color: #6941c6;
}

.cppc__btn--primary {
  background: #7f56d9;
  border-color: #7f56d9;
  color: #ffffff;
}

.cppc__btn:disabled,
.cppc__icon-btn:disabled,
.cppc__edit-btn:disabled,
.cppc__expand-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.cppc__btn-icon {
  display: inline-flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cppc__btn-icon img {
  display: block;
  width: 20px;
  height: 20px;
}

.cppc__icon-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.cppc__icon-btn,
.cppc__menu-icon {
  cursor: pointer;
}

.cppc__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
}

.cppc__metric-row {
  display: flex;
  gap: 8px;
  width: 100%;
}

.cppc__metric-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 232px;
  padding: 24px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
}

.cppc__metric-tile--salary {
  flex: 0 0 232px;
}

.cppc__metric-tile--readiness {
  flex: 1 1 0;
  min-width: 0;
}

.cppc__metric-tile--fair-pay {
  flex: 0 0 232px;
}

.cppc__metric-label {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #475467;
}

.cppc__metric-value {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 30px;
  font-weight: 600;
  line-height: 38px;
  color: #101828;
}

.cppc__edit-btn {
  position: absolute;
  top: 19px;
  right: 19px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.cppc__edit-btn img {
  display: block;
  width: 20px;
  height: 20px;
}

.cppc__delta-banner {
  width: 100%;
  padding: 5px 11px;
  border-radius: 999px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
}

.cppc__delta-banner--inside {
  background: #ecfdf3;
  border: 1px solid #abefc6;
  color: #067647;
}

.cppc__delta-banner--outside {
  background: #fffcf5;
  border: 1px solid #fedf89;
  color: #b54708;
}

.cppc__lower-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.cppc__readiness {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.cppc__readiness-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
}

.cppc__readiness-pct {
  font-family: Inter, sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: -0.48px;
  color: #344054;
}

.cppc__readiness-label,
.cppc__readiness-as-of {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #475467;
}

.cppc__readiness-as-of {
  min-width: 0;
  text-align: right;
  margin-left: 35px;
}

.cppc__readiness-track {
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: #eaecf0;
  overflow: hidden;
}

.cppc__readiness-fill {
  height: 100%;
  border-radius: 999px;
  background: #12b76a;
}

.cppc__position-strip {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 6px 13px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  box-sizing: border-box;
}

.cppc__position-caption {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: normal;
  color: #344054;
}

.cppc__position-link,
.cppc__pay-range-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.cppc__position-link {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
}

.cppc__position-icon {
  flex-shrink: 0;
}

.cppc__readiness-ladder {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  color: #667085;
}

.cppc__pay-range-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cppc__pay-range-link {
  gap: 7px;
}

.cppc__pay-range-icon {
  display: block;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.cppc__pay-range-code {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #101828;
}

.cppc__pay-range-name {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: #101828;
}

.cppc__pay-range-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
}

.cppc__pay-range-meta-left {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-right: 0;
  font-family: Inter, sans-serif;
  font-size: 10px;
  font-weight: 500;
  line-height: 20px;
  color: #101828;
}

.cppc__flag {
  width: 12px;
  height: 12px;
  object-fit: cover;
  flex-shrink: 0;
}

.cppc__meta-sep {
  display: block;
  width: 14px;
  height: 20px;
  flex-shrink: 0;
}

.cppc__ladder-values {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.cppc__ladder-value {
  display: inline-flex;
  align-items: center;
}

.cppc__ladder-dash {
  margin-right: 5px;
  color: #344054;
}

.cppc__tone--neutral {
  color: #344054;
}

.cppc__tone--danger {
  color: #ff0303;
}

.cppc__tone--success {
  color: #17b26a;
}

.cppc__tone--info {
  color: #00a3ff;
}

.cppc__version-wrap {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.cppc__version-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border: 1px solid #eaecf0;
  border-radius: 6px;
  background: #f9fafb;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
}

.cppc__version-date {
  font-family: Inter, sans-serif;
  font-size: 10px;
  font-weight: 500;
  line-height: 20px;
  color: #101828;
  white-space: nowrap;
}

.cppc__reconciliation-footer {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 6px 13px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  box-sizing: border-box;
}

.cppc__reconciliation-text {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: normal;
  color: #344054;
}

.cppc__expand-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
}

.cppc__expand-btn img {
  display: block;
  width: 24px;
  height: 24px;
}

.cppc__skeleton {
  border-radius: 8px;
  background: linear-gradient(90deg, #f2f4f7 25%, #eaecf0 50%, #f2f4f7 75%);
  background-size: 200% 100%;
  animation: cppc-shimmer 1.4s ease infinite;
}

.cppc__skeleton--value {
  width: 120px;
  height: 38px;
}

@keyframes cppc-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 960px) {
  .cppc__summary-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .cppc__header-actions {
    flex-wrap: wrap;
    width: 100%;
  }

  .cppc__metric-row {
    flex-direction: column;
  }

  .cppc__metric-tile--salary,
  .cppc__metric-tile--fair-pay {
    flex: 1 1 auto;
    width: 100%;
  }

  .cppc__meta-line,
  .cppc__delta-banner {
    white-space: normal;
  }
}
</style>
